import { format } from 'date-fns'
import type { IReceiptReportLine } from '../../../../../../modules/API/Reciept/models/receipt'
import { isValidYmdDate } from '../../../../../../generic/utils/forDate/isValidYmdDate'
import { PAYMENT_TYPE, PaymentType } from '../../../receipt/generic/getDetailesByPaymentType'
import type { IPaymentTypeShare, IReceiptsReportStats } from '../types/stats'

const PAYMENT_TYPES = Object.keys(PAYMENT_TYPE) as PaymentType[]
const CHEQUE: PaymentType = 'ש'

const sumOf = (lines: IReceiptReportLine[]): number => lines.reduce((acc, line) => acc + line.Sum, 0)

/**
 * Canceling is per line: a receipt may keep its transfer while its cheque is canceled.
 * Canceled lines count only towards canceledCount/canceledSum; a receipt counts while it has a live line.
 */
export const calcReceiptsReportStats = (lines: IReceiptReportLine[], today: Date): IReceiptsReportStats => {
  const live = lines.filter((line) => !line.Canceled)
  const canceled = lines.filter((line) => line.Canceled)

  const totalSum = sumOf(live)
  const receiptsCount = new Set(live.map((line) => line.RecieptNum)).size

  const byPaymentType = Object.fromEntries(
    PAYMENT_TYPES.map((type): [PaymentType, IPaymentTypeShare] => {
      const sum = sumOf(live.filter((line) => line.PaymentType === type))
      return [type, { sum, percent: totalSum ? (sum / totalSum) * 100 : 0 }]
    }),
  ) as Record<PaymentType, IPaymentTypeShare>

  // Dates are yyyyMMdd strings, so they compare as strings; empty and zero placeholders are skipped.
  const todayYmd = format(today, 'yyyyMMdd')
  const pendingCheques = live.filter(
    (line) => line.PaymentType === CHEQUE && isValidYmdDate(line.ValueDate) && line.ValueDate > todayYmd,
  )

  return {
    totalSum,
    receiptsCount,
    averageReceipt: receiptsCount ? totalSum / receiptsCount : 0,
    byPaymentType,
    pendingChequesSum: sumOf(pendingCheques),
    canceledCount: canceled.length,
    canceledSum: sumOf(canceled),
  }
}

/** Search by customer, seller or receipt number. */
export const filterReceiptLines = (lines: IReceiptReportLine[], query: string): IReceiptReportLine[] => {
  const q = query.trim().toLowerCase()
  if (!q) return lines
  return lines.filter(
    (line) =>
      line.CustomerName.toLowerCase().includes(q) ||
      line.SellerName.toLowerCase().includes(q) ||
      String(line.RecieptNum).includes(q),
  )
}
